import { encodeFunctionData } from "viem"
import { useAccount, useEstimateGas, useGasPrice } from "wagmi"

import { selectIsConnected, useDappSelector } from "#/redux-state"
import {
  bridge,
  ETHEREUM_CHAIN_ID,
  ONE_SECOND_IN_MILLISECONDS,
} from "#/shared/constants"
import MezoBridge from "#/shared/constants/artifacts/MezoBridge"
import { BridgeableTokens } from "#/shared/types"

import {
  useAllowanceERC20Token,
  useEstimateApproveERC20TokenGas,
} from "../erc-20"

const refetchInterval = ONE_SECOND_IN_MILLISECONDS * 10 // 10 seconds
const staleTime = refetchInterval

export default function useEstimateBridgeERC20Gas(
  token: Exclude<BridgeableTokens, "BTC">,
  amount?: bigint,
  isEnabled = true,
) {
  const { address } = useAccount()
  const isConnected = useDappSelector(selectIsConnected)
  const { sourceToken } = bridge.BRIDGEABLE_TOKENS[token]

  const functionName = token === "tBTC" ? "bridgeTBTC" : "bridgeERC20"
  const commonArgs = [amount ?? 0n, address!] as const
  const bridgeErc20Args = [sourceToken, ...commonArgs] as const
  const args = token === "tBTC" ? commonArgs : bridgeErc20Args
  const isQueryEnabled = !!address && isConnected && !!amount && isEnabled

  const { data: gasAmount, isFetching: isFetchingGasAmount } = useEstimateGas({
    to: MezoBridge.address,
    data: encodeFunctionData({
      abi: MezoBridge.abi,
      functionName,
      args,
    }),
    chainId: ETHEREUM_CHAIN_ID,
    query: {
      enabled: isQueryEnabled,
      // We hardcode the gas because we can't estimate the gas without
      // `erc20.approve` transaction. In case the user doesn't have enough
      // approved amount (then `useEstimateGas` will fail and default value will
      // be used). In some cases users may have already approved amount or
      // infinite approval, so this hook will dynamically estimate the gas.
      initialData: 67885n,
      staleTime,
      refetchInterval,
    },
  })

  const { data: gasPrice, isFetching: isFetchingGasPrice } = useGasPrice({
    chainId: ETHEREUM_CHAIN_ID,
    query: {
      enabled: !!gasAmount && isQueryEnabled,
      staleTime,
      refetchInterval,
    },
  })

  const {
    estimatedTransactionFee: estimatedApproveTransactionFee,
    isFetching: isFetchingApproveTransactionFee,
  } = useEstimateApproveERC20TokenGas(
    sourceToken,
    MezoBridge.address,
    amount ?? 0n,
    ETHEREUM_CHAIN_ID,
  )

  const { data: approvedAmount, isFetching: isFetchingApprovedAmount } =
    useAllowanceERC20Token(sourceToken, MezoBridge.address, ETHEREUM_CHAIN_ID)

  const shouldBeApprovedAmount =
    approvedAmount !== undefined && approvedAmount < (amount ?? 0n)

  const estimatedBridgeTransactionFee = (gasAmount ?? 0n) * (gasPrice ?? 0n)

  return {
    isFetching:
      isFetchingGasAmount ||
      isFetchingGasPrice ||
      isFetchingApproveTransactionFee ||
      isFetchingApprovedAmount,
    gas: gasAmount,
    gasPrice,
    estimatedTransactionFee: shouldBeApprovedAmount
      ? estimatedApproveTransactionFee + estimatedBridgeTransactionFee
      : estimatedBridgeTransactionFee,
  }
}
